import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ReplyIcon from '@mui/icons-material/Reply';
import EmojiPicker from 'emoji-picker-react';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

export default function ChatInput({ onSend, disabled, replyTo, onCancelReply }) {
  const [text, setText] = useState('');
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const inputRef = useRef(null);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed, replyTo?.id || null);
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (emojiData) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    const newText = text.slice(0, start) + emojiData.emoji + text.slice(end);
    setText(newText);
    setEmojiAnchor(null);
    setTimeout(() => {
      input?.focus();
      const pos = start + emojiData.emoji.length;
      input?.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(13,27,42,0.95)', pb: 'env(safe-area-inset-bottom)' }}>
      {replyTo && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backgroundColor: 'rgba(255,255,255,0.03)',
        }}>
          <ReplyIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 16, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ color: DEFAULT_THEME_COLOR, fontWeight: 600 }}>@{replyTo.sender_username}</span>
            {' — '}
            {replyTo.content}
          </Typography>
          <IconButton data-testid="cancel-reply" onClick={onCancelReply} size="small" sx={{ color: 'rgba(255,255,255,0.4)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, p: 1 }}>
        <IconButton
          onClick={(e) => setEmojiAnchor(e.currentTarget)}
          sx={{ color: 'rgba(255,255,255,0.45)', mb: 0.5, '&:hover': { color: DEFAULT_THEME_COLOR } }}
        >
          <EmojiEmotionsOutlinedIcon />
        </IconButton>

        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          inputProps={{ maxLength: 2000 }}
          inputRef={inputRef}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'white',
              fontSize: '0.9rem',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              '&.Mui-focused fieldset': { borderColor: DEFAULT_THEME_COLOR },
            },
          }}
        />

        <IconButton
          onClick={handleSend}
          disabled={!text.trim() || disabled}
          sx={{ color: text.trim() && !disabled ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.3)', mb: 0.5 }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { backgroundColor: 'transparent', boxShadow: 'none' } }}
      >
        <Box sx={{ maxWidth: 'min(340px, 90vw)' }}>
          <EmojiPicker onEmojiClick={handleEmojiClick} theme="dark" lazyLoadEmojis width="100%" height={350} />
        </Box>
      </Popover>
    </Box>
  );
}
